import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "pref:push-enabled";

function readPref(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Module-level store rather than per-component `useState`.
 *
 * The toggle lives in Settings while the consumer that actually raises
 * notifications lives in AuthenticatedApp. With local state each got its own
 * copy, so flipping the toggle did nothing until a full page reload.
 */
let current = readPref();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return current;
}

export function setPushPreference(value: boolean): void {
  if (current === value) return;
  current = value;
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    /* private mode / blocked storage — in-memory value still applies */
  }
  listeners.forEach((l) => l());
}

export function usePushPreference(): [boolean, (v: boolean) => void] {
  const enabled = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const update = useCallback((v: boolean) => setPushPreference(v), []);
  return [enabled, update];
}
